import { observer } from 'mobx-react'
import React, { useState, useEffect, useMemo } from 'react'
import cx from 'classnames'
import { Link, useParams, useNavigate } from 'react-router-dom'
import rootStore from '../store'
import { useKnowledgeBaseById } from '@/features/knowledge-bases/hooks'
import LinearProgress from '@/ui/LinearProgress'
import {
  bytesToGB,
  LOCAL_STORAGE_PROVIDER,
  TOTAL_KB_STORAGE,
} from '@/features/knowledge-bases/domain'
import TransitionLayout from '@/ui/TransitionLayout'
import CantAccessKnowledgeBase from '@/components/CantAccessKnowledgeBase'
import { Icons, Icon } from '@/ui/icon'
import Heading from '@/ui/Heading'
import ColoredBox from '@/ui/ColoredBox'
import Switch from '@/ui/form/Switch'
import CTA from '@/ui/CTA'
import Spinner from '@/ui/Spinner'
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableCellActions,
  TableCellButton,
  TableSearch,
} from '@/ui/table'
import { useTableSearch, useTableSort } from '@/ui/table/hooks'
import Progress from '@/ui/Progress'
import Dialog from '@/ui/dialogs/Dialog'
import { EmptyMessage } from '@/ui/EmptyMessage'
import { useDialogRef } from '@/utils/dialog'
import { toDisplayDate } from '@/utils/dates'
import { Space } from '@/features/spaces/types'
import { checkRAGAccess } from '@/features/knowledge-bases/common'
import { toast } from 'react-toastify'
import {
  KnowledgeBaseModel,
  MASKED_GLOBAL_TOOLKIT_ID,
} from '@/features/knowledge-bases/api'

export const KnowledgeBaseDetailPage = observer(() => {
  const params = useParams()
  const knowledgeBaseId = params.id
  const navigate = useNavigate()

  const { organizations, auth, knowledgeBases, spaces, departments } = rootStore

  const canAccessRAG = checkRAGAccess(
    knowledgeBaseId,
    organizations.planInfo,
    auth.user!,
  )

  // Admins accessing global KB should have RAG enabled by default
  const isAdminAccessingGlobalKB =
    knowledgeBaseId === 'global' && auth.user?.role === 'admin'

  const hasRAGEnabled =
    isAdminAccessingGlobalKB ||
    (canAccessRAG && organizations.planInfo?.hasRAGEnabled)

  const [ragEnabledSwitchValue, setHasRAGEnabledValue] = useState(
    !!organizations.planInfo?.hasRAGEnabled,
  )
  const [newSpace, setNewSpace] = useState('')

  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [editingValue, setEditingValue] = useState<string>('')

  // Filter out GLOBAL_KB_SPACE when already viewing the global KB
  const displayedSpaces = isAdminAccessingGlobalKB
    ? spaces.allSpacesWithPublic.filter(
        (space) => space.id !== 'global-documents',
      )
    : spaces.allSpacesWithPublic

  const { filteredData: filteredSpaces, handleSearch: handleSearchSpaces } =
    useTableSearch(displayedSpaces, ['displayName'])
  const { sortedData, sortConfig, handleSort } = useTableSort(filteredSpaces)

  const {
    dialogRef: shareDialogRef,
    handleOpen: openShareDialog,
    handleClose: closeShareDialog,
  } = useDialogRef()

  const [selectedSpaceIdToDelete, setSelectedSpaceIdToDelete] = useState<
    string | null
  >(null)

  const {
    dialogRef: deleteSpaceDialogRef,
    handleOpen: openDeleteSpaceDialog,
    handleClose: closeDeleteSpaceDialog,
  } = useDialogRef()

  // Determine if Spaces are enabled based on the plan info
  // Admins accessing global KB should have spaces enabled by default
  const hasSpacesEnabled =
    isAdminAccessingGlobalKB || organizations.planInfo?.enableSpaces || false

  // Load spaces and departments when the component mounts if spaces are enabled
  useEffect(() => {
    if (hasSpacesEnabled) {
      // Only fetch if not already loaded
      if (!spaces.initiallyLoaded) {
        spaces.fetchSpaces()
      }

      // Only fetch departments if not already loaded
      if (!departments.initiallyLoaded) {
        departments.fetchDepartments()
      }
    }
  }, [hasSpacesEnabled, spaces.initiallyLoaded, departments.initiallyLoaded])

  const { knowledgeBase } = useKnowledgeBaseById(
    hasRAGEnabled ? knowledgeBaseId : undefined,
    hasRAGEnabled,
  )

  const [globalKBSize, setGlobalKBSize] = useState(0)

  useEffect(() => {
    // Fetch global KB size to include in storage calculations
    knowledgeBases
      .fetchById(MASKED_GLOBAL_TOOLKIT_ID)
      .then((info: KnowledgeBaseModel) => {
        setGlobalKBSize(info.total_documents_size)
      })
      .catch(() => {
        // Silently fail if global KB not accessible
        setGlobalKBSize(0)
      })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const maxKBStorage = useMemo(() => {
    const realSize = knowledgeBase?.max_size
      ? bytesToGB(knowledgeBase.max_size)
      : 0

    return Math.max(realSize, TOTAL_KB_STORAGE)
  }, [knowledgeBase?.max_size])

  const totalUsedSize = useMemo(() => {
    const orgSize = knowledgeBase?.total_documents_size || 0
    return orgSize + globalKBSize
  }, [knowledgeBase?.total_documents_size, globalKBSize])

  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>()

  if (!canAccessRAG) {
    return <CantAccessKnowledgeBase />
  }

  if (
    hasRAGEnabled &&
    knowledgeBaseId &&
    !knowledgeBases.knowledgeBaseCache[knowledgeBaseId]
  ) {
    return (
      <div className="top-loading-bar">
        <LinearProgress />
      </div>
    )
  }

  const handleOpenShareDialog = (space: Space) => {
    spaces.setSelectedSpaceId(space.id)
    setSelectedDepartmentIds(space.departments?.map((d) => d.id) ?? [])
    openShareDialog()
  }

  const handleCloseAndClearShareDialog = () => {
    spaces.setSelectedSpaceId(null)
    setSelectedDepartmentIds([])
    closeShareDialog()
  }

  const handleSpaceSharingSave = async () => {
    if (!spaces.selectedSpace) {
      return
    }

    const newSelectedDepartments = selectedDepartmentIds ?? []

    await spaces.updateSpaceDepartments(
      spaces.selectedSpace.id,
      newSelectedDepartments,
    )

    handleCloseAndClearShareDialog()
  }

  const handleCreateSpace = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    await spaces.createSpace(newSpace)
    setNewSpace('')
  }

  const handleOpenDeleteSpaceDialog = (spaceId: string) => {
    setSelectedSpaceIdToDelete(spaceId)
    openDeleteSpaceDialog()
  }

  const handleDeleteSpace = async () => {
    if (!selectedSpaceIdToDelete) {
      return
    }

    await spaces.deleteSpace(selectedSpaceIdToDelete).finally(() => {
      setSelectedSpaceIdToDelete(null)
      closeDeleteSpaceDialog()
    })
  }

  const handleClickSpace = (space: Space) => {
    // Special handling for global KB space
    if (space.id === 'global-documents') {
      navigate('/knowledge-bases/global/spaces/public', {
        state: { originKnowledgeBaseId: knowledgeBaseId },
      })
      return
    }
    navigate(`/knowledge-bases/${knowledgeBaseId}/spaces/${space.id}`)
  }

  const handleStartEditing = (index: number, value: string) => {
    setEditingIndex(index)
    setEditingValue(value)
  }

  const handleConfirmEditing = async (e: React.MouseEvent, space: Space) => {
    e.stopPropagation()

    if (editingIndex === null) {
      return
    }

    await spaces.updateSpace(space.id, editingValue)

    setEditingIndex(null)
    setEditingValue('')
  }

  const handleCancelEditing = (e: React.MouseEvent) => {
    e.stopPropagation()
    setEditingIndex(null)
    setEditingValue('')
  }

  return (
    <TransitionLayout>
      <div className="knowledge-page" data-cy="knowledge-base-page">
        <Heading
          variant="system"
          title="Base de conocimiento"
          subtitle="Esta integración permite a AiKit acceder a información de la base de conocimiento para dar respuestas personalizadas a través de la herramienta experto en tu empresa."
          size="large"
        />
        <div className="knowledge-page-row margin-top">
          {knowledgeBase && (
            <ColoredBox align="center">
              <div className="knowledge-page-row-box">
                <p className={cx('status-chip', knowledgeBase.indexer_status)}>
                  {knowledgeBase.indexer_status === 'idle' ? (
                    <>
                      <Icon size={16} name={Icons.Check} /> Contenido Indexado
                    </>
                  ) : knowledgeBase.indexer_status === 'active' ? (
                    <>
                      <Spinner isVisible /> Indexando...
                    </>
                  ) : knowledgeBase.indexer_status === 'error' ? (
                    <>
                      <Icon size={16} name={Icons.Error} /> Error
                    </>
                  ) : null}
                  {knowledgeBase.storage_provider_config.provider !==
                  LOCAL_STORAGE_PROVIDER ? (
                    <span className={cx('status-badge', 'syncing')}>
                      {knowledgeBase.sync_status === 'synced'
                        ? 'Sincronizado'
                        : knowledgeBase.sync_status === 'syncing'
                          ? 'Sincronizando...'
                          : knowledgeBase.sync_status === 'error'
                            ? 'Error de Sincronización'
                            : null}
                    </span>
                  ) : null}
                </p>
                <p className="knowledge-page-row-box-text">
                  Última actualización:{' '}
                  <time
                    dateTime={new Date(
                      knowledgeBase.updated_at,
                    ).toLocaleString()}
                    style={{
                      font: 'var(--label-small)',
                    }}
                  >
                    {new Date(knowledgeBase.updated_at).toLocaleString()}
                  </time>
                </p>
              </div>
            </ColoredBox>
          )}

          <ColoredBox variant="colored" align="center" direction="row">
            <Switch
              id="integrationActived"
              label="Integración activada"
              checked={ragEnabledSwitchValue}
              onChange={() => setHasRAGEnabledValue((prev) => !prev)}
            />
            <CTA
              size="medium"
              variant="system"
              type="button"
              onClick={() => {
                organizations
                  .updatePlan({
                    hasRAGEnabled: !!ragEnabledSwitchValue,
                  })
                  .then(() => {
                    toast.success('Configuración actualizada correctamente')
                  })
              }}
              isLoading={organizations.isUpdatingPlan}
            >
              Guardar
            </CTA>
          </ColoredBox>
        </div>

        {knowledgeBase && (
          <div className="knowledge-page-block">
            <Heading
              variant="system"
              title="Espacios"
              subtitle="A continuación podrás crear y gestionar los espacios de documentos a los que tendrá acceso AiKit y definir qué departamentos pueden acceder cada uno de ellos."
              size="large"
            />

            {spaces.allSpacesWithPublic.length > 9 && (
              <TableSearch
                onSearch={handleSearchSpaces}
                placeholder="Introduce el nombre de la persona que quieres buscar"
              />
            )}
            <Table>
              <colgroup>
                <col />
                <col />
                <col className="table-col-shrink" />
              </colgroup>
              <TableHeader>
                <TableHeaderCell
                  column="name"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                >
                  Nombre
                </TableHeaderCell>
                <TableHeaderCell
                  column="lastModification"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                >
                  Última modificación
                </TableHeaderCell>
                <TableHeaderCell align="center">Acciones</TableHeaderCell>
              </TableHeader>
              <TableBody>
                {sortedData.length > 0 ? (
                  sortedData.map((item) => {
                    const isPublic = item.id === 'public'
                    const isGlobalKB = item.id === 'global-documents'
                    const isReadOnly =
                      isPublic || (isGlobalKB && auth.user?.role !== 'admin')
                    const realIndex = spaces.allSpacesWithPublic.findIndex(
                      (entry) => entry.id === item.id,
                    )
                    const isRowEditing = editingIndex === realIndex

                    return (
                      <TableRow
                        key={item.id}
                        onClick={() => handleClickSpace(item)}
                      >
                        <TableCell isEditing={isRowEditing}>
                          {isRowEditing ? (
                            <>
                              <input
                                className="table-editable-cell"
                                value={editingValue}
                                onChange={(e) =>
                                  setEditingValue(e.target.value)
                                }
                                onClick={(e) => e.stopPropagation()}
                              />
                              <div className="view-navigation-form-input-actions">
                                <button
                                  className="view-navigation-form-input-button button-accept"
                                  type="button"
                                  onClick={(e) => handleConfirmEditing(e, item)}
                                >
                                  <Icon size={14} name={Icons.Check} />
                                  <span className="sr-only">Añadir</span>
                                </button>
                                <button
                                  className="view-navigation-form-input-button button-cancel"
                                  type="button"
                                  onClick={handleCancelEditing}
                                >
                                  <Icon size={14} name={Icons.Error} />
                                  <span className="sr-only">Cancelar</span>
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              {item.displayName}
                              {isGlobalKB && auth.user?.role !== 'admin' && (
                                <span
                                  style={{ marginLeft: '8px', opacity: 0.6 }}
                                >
                                  <Icon size={14} name={Icons.Info} />
                                </span>
                              )}
                            </>
                          )}
                        </TableCell>
                        <TableCell>{toDisplayDate(item.updatedAt)}</TableCell>

                        <TableCellActions gap="small" align="center">
                          {!isReadOnly && (
                            <>
                              <TableCellButton
                                onClick={() => handleOpenShareDialog(item)}
                              >
                                <Icon size={14} name={Icons.AddUser} />
                                <span className="sr-only">
                                  Compartir espacio con departamento
                                </span>
                              </TableCellButton>

                              <TableCellButton
                                onClick={() =>
                                  handleStartEditing(
                                    realIndex,
                                    item.displayName,
                                  )
                                }
                              >
                                <Icon size={14} name={Icons.Edit} />
                                <span className="sr-only">Editar</span>
                              </TableCellButton>

                              <TableCellButton
                                onClick={() =>
                                  handleOpenDeleteSpaceDialog(item.id)
                                }
                              >
                                <Icon size={14} name={Icons.Trash} />
                                <span className="sr-only">Eliminar</span>
                              </TableCellButton>
                            </>
                          )}
                        </TableCellActions>
                      </TableRow>
                    )
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={3}>
                      No hay datos que coincidan con esa búsqueda
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {knowledgeBase && (
          <div className="knowledge-page-row">
            <Progress
              label="Capacidad"
              description={`Total almacenamiento ${maxKBStorage}GB (${((bytesToGB(totalUsedSize) / maxKBStorage) * 100).toFixed(1)}%)`}
              value={bytesToGB(totalUsedSize)}
              max={maxKBStorage}
            />
            {knowledgeBase.storage_provider_config.provider ===
            LOCAL_STORAGE_PROVIDER ? (
              <div>
                <form onSubmit={handleCreateSpace}>
                  <div className="formControl">
                    <label className="formControl-label">
                      Crear un espacio
                    </label>
                    <div className="formControl-input-container">
                      <input
                        className="formControl-input small"
                        type="text"
                        placeholder="Introduce el nombre del espacio"
                        value={newSpace}
                        onChange={(e) => setNewSpace(e.target.value)}
                      />
                      <CTA
                        variant="system"
                        size="medium"
                        type="submit"
                        isDisabled={!newSpace}
                        isLoading={spaces.isCreatingSpace}
                      >
                        Crear
                      </CTA>
                    </div>
                  </div>
                </form>
              </div>
            ) : null}
          </div>
        )}

        <Dialog
          ref={shareDialogRef}
          title="Compartir con departamentos"
          text="Añade o elimina los departamentos que pueden tener acceso a los documentos almacenados en este espacio."
          icon={Icons.AddUser}
          onClose={handleCloseAndClearShareDialog}
          blockClose={spaces.isSpaceUpdating(
            spaces.selectedSpace?.id as string,
          )}
          actions={
            <>
              <CTA
                size="medium"
                onClick={handleCloseAndClearShareDialog}
                isDisabled={spaces.isSpaceUpdating(
                  spaces.selectedSpace?.id as string,
                )}
              >
                Cancelar
              </CTA>
              <CTA
                variant="system"
                size="medium"
                onClick={handleSpaceSharingSave}
                isLoading={spaces.isSpaceUpdating(
                  spaces.selectedSpace?.id as string,
                )}
              >
                Guardar
              </CTA>
            </>
          }
        >
          <EmptyMessage>
            Para poder compartir los documentos de este espacio con miembros de
            tu organización antes debes crear{' '}
            <Link to="/organization/departments">departamentos</Link>. Después,
            podrás añadirlos a los diferentes espacios.
          </EmptyMessage>
          <Table>
            <colgroup>
              <col />
              <col className="table-col-shrink" />
            </colgroup>
            <TableHeader>
              <TableHeaderCell>Nombre</TableHeaderCell>
              <TableHeaderCell align="center">Acción</TableHeaderCell>
            </TableHeader>
            <TableBody>
              {departments.departments.map((item) => {
                if (!spaces.selectedSpace) return null

                const shared = selectedDepartmentIds?.includes(item.id)

                return (
                  <TableRow key={item.id}>
                    <TableCell>{item.name}</TableCell>
                    <TableCellActions align="center">
                      <CTA
                        variant={shared ? 'destructive' : 'system'}
                        size="small"
                        onClick={() => {
                          if (shared) {
                            setSelectedDepartmentIds((selected) =>
                              selected?.filter((d) => d !== item.id),
                            )
                          } else {
                            setSelectedDepartmentIds((selected) => [
                              ...(selected ?? []),
                              item.id,
                            ])
                          }
                        }}
                      >
                        {shared ? 'Eliminar' : 'Compartir'}
                      </CTA>
                    </TableCellActions>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Dialog>

        <Dialog
          ref={deleteSpaceDialogRef}
          title="Eliminar espacio"
          text="¿Estás seguro de querer eliminar este espacio? Se eliminarán todos los documentos almacenados en él."
          icon={Icons.Trash}
          onClose={closeDeleteSpaceDialog}
          blockClose={spaces.isSpaceDeleting(selectedSpaceIdToDelete as string)}
          actions={
            <>
              <CTA
                size="medium"
                onClick={closeDeleteSpaceDialog}
                isDisabled={spaces.isSpaceDeleting(
                  selectedSpaceIdToDelete as string,
                )}
              >
                Cancelar
              </CTA>
              <CTA
                variant="destructive"
                size="medium"
                onClick={handleDeleteSpace}
                isLoading={spaces.isSpaceDeleting(
                  selectedSpaceIdToDelete as string,
                )}
              >
                Eliminar
              </CTA>
            </>
          }
        />
      </div>
    </TransitionLayout>
  )
})
